/** ブラウザ連携の共有入力契約。端末と Lambda の両方から使う (依存ゼロ)。 */
export const LINK_CLIENTS = ['claude', 'codex'] as const;
export type LinkClient = (typeof LINK_CLIENTS)[number];
export const LINK_SCOPES = [
  'read',
  'read write',
  'read review',
  'read write review',
  'read upload',
  'read write upload',
  'read review upload',
  'read write review upload',
  'read gui',
  'read write gui',
  'read review gui',
  'read write review gui',
  'read upload gui',
  'read write upload gui',
  'read review upload gui',
  'read write review upload gui',
] as const;
export type LinkScope = (typeof LINK_SCOPES)[number];
export const LINK_CLIENT_LABELS: Record<LinkClient, string> = {
  claude: 'Claude',
  codex: 'Codex',
};
export const LINK_CALLBACK_PATH = '/acecollect/callback';
export const LINK_CODE_SECONDS = 90;
export const LINK_ACCESS_SECONDS = 15 * 60;
export const LINK_REFRESH_SECONDS = 30 * 24 * 60 * 60;
export const LINK_REQUEST_TIMEOUT_MS = 30_000;

export class LinkError extends Error {
  constructor(
    readonly code: string,
    readonly status = 400,
  ) {
    super(code);
    this.name = 'LinkError';
  }
}

export function isLinkClient(value: unknown): value is LinkClient {
  return value === 'claude' || value === 'codex';
}
export function isLinkScope(value: unknown): value is LinkScope {
  return typeof value === 'string' && (LINK_SCOPES as readonly string[]).includes(value);
}
export function hasLinkWriteScope(scope: LinkScope): boolean {
  return isLinkScope(scope) && scope.split(' ').includes('write');
}
export function hasLinkReviewScope(scope: LinkScope): boolean {
  return isLinkScope(scope) && scope.split(' ').includes('review');
}
export function hasLinkUploadScope(scope: LinkScope): boolean {
  return isLinkScope(scope) && scope.split(' ').includes('upload');
}
export function hasLinkGuiScope(scope: LinkScope): boolean {
  return isLinkScope(scope) && scope.split(' ').includes('gui');
}
export function selectedLinkScope(
  write: boolean,
  review: boolean,
  upload = false,
  gui = false,
): LinkScope {
  return [
    'read',
    ...(write ? ['write'] : []),
    ...(review ? ['review'] : []),
    ...(upload ? ['upload'] : []),
    ...(gui ? ['gui'] : []),
  ].join(' ') as LinkScope;
}
export function isLinkRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function hasLinkControl(value: string): boolean {
  return [...value].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);
}
export function linkString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string') throw new LinkError('invalid_request');
  return value;
}
export function validCallbackUri(value: string): boolean {
  try {
    const url = new URL(value);
    const port = Number(url.port);
    return (
      url.protocol === 'http:' &&
      url.hostname === '127.0.0.1' &&
      Number.isInteger(port) &&
      port >= 1024 &&
      port <= 65535 &&
      url.pathname === LINK_CALLBACK_PATH &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      url.href === value
    );
  } catch {
    return false;
  }
}
export interface LinkAuthorizationRequest {
  client: LinkClient;
  redirectUri: string;
  codeChallenge: string;
  state: string;
  scope: LinkScope;
  deviceName: string;
  reviewCapability?: '1';
  uploadCapability?: '1';
  guiCapability?: '1';
}
export function parseLinkAuthorization(value: unknown): LinkAuthorizationRequest {
  if (!isLinkRecord(value)) throw new LinkError('invalid_request');
  const { client, scope } = value;
  const redirectUri = linkString(value, 'redirectUri');
  const codeChallenge = linkString(value, 'codeChallenge');
  const state = linkString(value, 'state');
  const deviceName = linkString(value, 'deviceName').trim();
  if (
    !isLinkClient(client) ||
    !isLinkScope(scope) ||
    (value.reviewCapability !== undefined && value.reviewCapability !== '1') ||
    (value.uploadCapability !== undefined && value.uploadCapability !== '1') ||
    (value.guiCapability !== undefined && value.guiCapability !== '1') ||
    (isLinkScope(scope) && hasLinkGuiScope(scope) && value.guiCapability !== '1') ||
    (isLinkScope(scope) && hasLinkReviewScope(scope) && value.reviewCapability !== '1') ||
    (isLinkScope(scope) && hasLinkUploadScope(scope) && value.uploadCapability !== '1') ||
    !validCallbackUri(redirectUri) ||
    value.codeChallengeMethod !== 'S256' ||
    !/^[A-Za-z0-9_-]{43}$/.test(codeChallenge) ||
    !/^[A-Za-z0-9_-]{43}$/.test(state) ||
    deviceName.length < 1 ||
    deviceName.length > 80 ||
    hasLinkControl(deviceName)
  )
    throw new LinkError('invalid_request');
  return {
    client,
    scope,
    redirectUri,
    codeChallenge,
    state,
    deviceName,
    ...(value.reviewCapability === '1' ? { reviewCapability: '1' as const } : {}),
    ...(value.uploadCapability === '1' ? { uploadCapability: '1' as const } : {}),
    ...(value.guiCapability === '1' ? { guiCapability: '1' as const } : {}),
  };
}
export interface LinkTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresAt: number;
  connectionId: string;
  client: LinkClient;
  scope: LinkScope;
}
export interface LinkConnectionSummary {
  connectionId: string;
  client: LinkClient;
  scope: LinkScope;
  deviceName: string;
  createdAt: number;
  expiresAt: number;
  revokedAt: number | null;
}
export function parseLinkTokens(value: unknown): LinkTokens {
  if (!isLinkRecord(value)) throw new LinkError('invalid_token_response');
  const accessToken = linkString(value, 'accessToken');
  const refreshToken = linkString(value, 'refreshToken');
  const connectionId = linkString(value, 'connectionId');
  const { expiresIn, refreshExpiresAt, client, scope } = value;
  if (
    !/^acm_a_[A-Za-z0-9_-]{43}$/.test(accessToken) ||
    !/^acm_r_[A-Za-z0-9_-]{43}$/.test(refreshToken) ||
    !/^[A-Za-z0-9-]{1,64}$/.test(connectionId) ||
    !isLinkClient(client) ||
    !isLinkScope(scope) ||
    typeof expiresIn !== 'number' ||
    expiresIn <= 0 ||
    expiresIn > LINK_ACCESS_SECONDS ||
    typeof refreshExpiresAt !== 'number' ||
    !Number.isSafeInteger(refreshExpiresAt)
  ) {
    throw new LinkError('invalid_token_response');
  }
  return { accessToken, refreshToken, expiresIn, refreshExpiresAt, connectionId, client, scope };
}
